import { AbstractControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { RecipeFormValues } from '../../retail-specification/models/form-values';
import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import { PUMP_NONE_CODE } from 'app/shared/pumping/pumping.constant';

/**
 * Function used to set/remove validators from an specific form control.
 * It can also trigger a re-validation of the form control value.
 *
 * @param addValidator Flag to determine whether a validator will be added or removed.
 * @param formControl Form control where the validators will be added, if applicable.
 * @param validator Type of validator to be added to the form control.
 * @param updateValidity Flag to determine if we need to recalculate the form control validation status.
 */
export function setValidatorPumpPrimer(
  addValidator: boolean,
  formControl: AbstractControl,
  validator: ValidatorFn,
  updateValidity: boolean = true,
): void {
  if (!formControl) {
    return;
  }
  formControl.markAsPristine();
  if (addValidator) {
    formControl.setValidators([validator]);
  } else {
    formControl.clearValidators();
    formControl.setErrors(null);
  }
  if (updateValidity) {
    formControl.updateValueAndValidity();
  }
}

/**
 * Function to evaluate form and define index positioned
 * @param formToEvaluate form is changed
 * @param currentForms current forms displayed/stored
 * @returns index position where currently is 'formToEvaluate'
 */
export function getIndexForm(formToEvaluate: UntypedFormGroup, currentForms: RecipeFormValues[]) {
  const formId = formToEvaluate.get(SpecFieldNames.ID).value;
  const recipe = currentForms.find((recipeForm) => recipeForm.id === formId);
  return currentForms.indexOf(recipe);
}

/**
 * Checks if the given Unload Type is a value that triggers the Pump Primer flow.
 *
 * @param unloadType The Unload Type selected.
 * @param unloadTypePumpValues List of Unload Types that trigger the Pump Primer flow.
 * @returns `true` if the Unload Type is found in the list of valid Unload Types, otherwise `false`.
 *
 */
export function isUnloadTypePumpValue(unloadType: string, unloadTypePumpValues: string[]): boolean {
  if (!unloadType || !unloadTypePumpValues || !Array.isArray(unloadTypePumpValues)) {
    return false;
  }
  return unloadTypePumpValues.includes(unloadType);
}

/**
 * Checks if the order has an Unload Type that triggers the Pump Primer flow.
 * Also determines if the order has a valid pump primer product.
 *
 * @param pumpPrimerProduct - The pump primer product value to validate.
 * @param unloadType - The unload type of the order.
 * @param unloadTypePumpValues - An array of strings representing valid Unload Types for a pump.
 * @returns `true` if the Unload Type is valid for a pump and the Pump Primer product is valid, otherwise `false`.
 *
 */
export function isOrderWithPumpPrimerProduct(
  pumpPrimerProduct: string,
  unloadType: string,
  unloadTypePumpValues: string[],
): boolean {
  const isUnloadTypePump = isUnloadTypePumpValue(unloadType, unloadTypePumpValues);
  const isValidPumpPrimerProduct = isPumpPrimerProductValueValid(pumpPrimerProduct);
  return isUnloadTypePump && isValidPumpPrimerProduct;
}

/**
 * Validates if all of the pump primer fields (`Quantity`, `Slump`, and `Arrival Before Delivery`) are valid.
 *
 * @param ppQuantity - The quantity of the pump primer.
 * @param ppSlump - The slump value of the pump primer.
 * @param ppAbd - The Arrival Before Delivery value of the pump primer.
 * @returns `true` if all fields (`ppQuantity`, `ppSlump`, and `ppAbd`) are provided and valid, otherwise `false`.
 *
 */
export function arePumpPrimerFieldsValid(ppQuantity: number, ppSlump: string, ppAbd: string): boolean {
  if (!ppQuantity || !ppSlump || !ppAbd) {
    return false;
  }
  return true;
}

/**
 * Validates if the given pump primer product value is set and not equal to `NONE`.
 *
 * @param ppProduct - The pump primer product value to validate.
 * @returns `true` if `ppProduct` is valid, otherwise `false`.
 *
 */
export function isPumpPrimerProductValueValid(ppProduct: string): boolean {
  return !!ppProduct && ppProduct !== PUMP_NONE_CODE;
}
